<template>
  <div class="notification-system">
    <!-- Debug Info (Remove in production) -->
    <!-- <div v-if="debugMode" class="debug-panel">
      <h4>🐛 Debug Info</h4>
      <p>User ID: {{ currentUserId }}</p>
      <p>API Status: {{ apiStatus }}</p>
      <p>Last Refresh: {{ lastRefresh }}</p>
      <p>Error: {{ lastError }}</p>
      <button @click="runDebug" class="debug-btn">Run Debug Check</button>
      <button @click="createTestNotification" class="debug-btn">Create Test Notification</button>
    </div> -->

    <!-- Notification Bell with Badge -->
    <div class="notification-bell" @click="toggleNotifications">
      <i class="fas fa-bell"></i>
      <span v-if="unreadCount > 0" class="notification-badge">
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
      <!-- Debug indicator -->
      <!-- <span v-if="debugMode && hasError" class="error-indicator">❌</span> -->
    </div>

    <!-- Notifications Dropdown -->
    <div v-if="showNotifications" class="notifications-dropdown" @click.stop>
      <div class="notifications-header">
        <div class="header-left">
          <h3><i class="fas fa-bell"></i> Notifications</h3>
          <span v-if="unreadCount > 0" class="unread-indicator">{{ unreadCount }} new</span>
        </div>
        <div class="header-actions">
          <!-- <button @click="toggleDebugMode" class="debug-toggle">🐛</button> -->
          <button v-if="unreadCount > 0" @click="markAllAsRead" class="mark-read-btn">
            <i class="fas fa-check-double"></i>
          </button>
          <button @click="refreshNotifications" class="refresh-btn" :disabled="loading">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Error Display -->
      <div v-if="lastError" class="error-display">
        <i class="fas fa-exclamation-triangle"></i>
        <span>{{ lastError }}</span>
        <button @click="clearError" class="clear-error">×</button>
      </div>

      <!-- Filter Tabs -->
      <div class="notification-filters">
        <button 
          v-for="filter in filters" 
          :key="filter.key"
          @click="handleFilterChange(filter.key)"
          :class="['filter-btn', { active: activeFilter === filter.key }]"
        >
          {{ filter.label }}
          <span v-if="filter.count > 0" class="filter-count">{{ filter.count }}</span>
        </button>
      </div>

      <div v-if="loading && notifications.length === 0" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        Loading notifications...
      </div>

      <div v-else-if="filteredNotifications.length === 0" class="empty-state">
        <i class="fas fa-inbox"></i>
        <p>{{ activeFilter === 'all' ? 'No notifications' : `No ${activeFilter} notifications` }}</p>
        <div v-if="debugMode" class="debug-info">
          <p>API called: {{ apiCallCount }} times</p>
          <p>Raw notifications: {{ notifications.length }}</p>
        </div>
      </div>

      <div v-else ref="notificationsList" class="notifications-list">
        <div 
            v-for="notification in filteredNotifications" 
            :key="notification.notification_id"
            :class="['notification-item', { 'unread': !notification.is_read }]"
            @click="handleNotificationClick(notification)"
          >
          <div class="notification-icon">
            <i :class="getNotificationIcon(notification.notification_type)"></i>
          </div>
          
          <div class="notification-content">
            <div class="notification-title">{{ notification.title }}</div>
            <div class="notification-message">{{ notification.message }}</div>
            
            <div class="notification-meta">
              <span class="notification-time">{{ formatTime(notification.created_at) }}</span>
              <span v-if="notification.metadata?.project_name" class="project-name">
                {{ notification.metadata.project_name }}
              </span>
            </div>
          </div>
          
          <div class="notification-actions">
            <button 
              v-if="!notification.is_read" 
              @click.stop="markAsRead([notification.notification_id])"
              class="mark-read-single"
              title="Mark as read"
            >
              <i class="fas fa-circle"></i>
            </button>
          </div>
        </div>
      </div>
      <div v-if="loadingMore" class="loading-more">
        <i class="fas fa-spinner fa-spin"></i> Loading more...
      </div>
      <!-- <div v-if="filteredNotifications.length > 10" class="notifications-footer">
        <router-link :to="getViewAllLink()" class="view-all-btn">
          View All ({{ filteredNotifications.length }})
        </router-link>
      </div> -->
    </div>

    <!-- Overlay to close dropdown -->
    <div v-if="showNotifications" class="dropdown-overlay" @click="closeNotifications"></div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DebugAgentNotifications',
  
  data() {
    return {
      offset: 0,  // Start by showing 10
    loadingMore: false,  // Flag for loading additional items
    hasMore: true,  // ✅ ADD

      notifications: [],
      counts: {
        total: 0,
        unread: 0,
        by_type: {}
      },
      loading: false,
      showNotifications: false,
      activeFilter: 'all',
      refreshInterval: null,
      userRole: null,
      
      // Debug-specific data
      debugMode: false,
      currentUserId: null,
      apiStatus: 'unknown',
      lastRefresh: null,
      lastError: null,
      hasError: false,
      apiCallCount: 0
    };
  },
  
  computed: {
    unreadCount() {
      return this.counts.unread || 0;
    },
    
    filters() {
      const baseFilters = [
        { key: 'all', label: 'All', count: this.counts.total }
      ];
      
      // Add type-specific filters based on available notification types
      Object.keys(this.counts.by_type || {}).forEach(type => {
        const typeData = this.counts.by_type[type];
        if (typeData.total > 0) {
          baseFilters.push({ 
            key: type, 
            label: this.getFilterLabel(type), 
            count: typeData.unread 
          });
        }
      });
      
      return baseFilters;
    },
    
    filteredNotifications() {
      if (this.activeFilter === 'all') {
        return this.notifications;
      }
      return this.notifications.filter(n => n.notification_type === this.activeFilter);
    }
  },
  watch: {
  showNotifications(isVisible) {
    if (isVisible) {
      // Wait for DOM to update with the dropdown
      this.$nextTick(() => {
        const list = this.$refs.notificationsList;
        if (list) {
          list.addEventListener('scroll', this.handleScroll);
          console.log('✅ Scroll listener attached to:', list);
        } else {
          console.error('❌ notificationsList ref not found!');
        }
      });
    }
  }
},
  async mounted() {
    // Enable debug mode if URL has debug parameter
    this.debugMode = new URLSearchParams(window.location.search).has('debug');
    
    await this.detectUserRole();
    await this.loadNotifications();
    
    // Refresh notifications every 30 seconds
    this.refreshInterval = setInterval(() => {
      if (!this.loading) {
        this.loadNotifications(false);
      }
    }, 10000);
    console.log('🔍 On mount, showNotifications:', this.showNotifications);
    console.log('🔍 On mount, ref exists:', !!this.$refs.notificationsList);
  },
  
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    const list = this.$refs.notificationsList;
  if (list) {
    list.removeEventListener('scroll', this.handleScroll);
    console.log('🧹 Scroll listener removed');
  }
  },
  
  methods: {
    handleScroll() {
  const list = this.$refs.notificationsList;
  if (!list || this.loadingMore || !this.hasMore) return;

  // console.log('📏 Scroll check:', {
  //   scrollHeight: list.scrollHeight,
  //   scrollTop: list.scrollTop,
  //   clientHeight: list.clientHeight,
  //   remaining: list.scrollHeight - list.scrollTop - list.clientHeight,
  //   notificationCount: this.notifications.length
  // });

  const bottomOfList = list.scrollHeight - list.scrollTop - list.clientHeight < 100;
  if (bottomOfList) {
    console.log('🎯 Bottom reached! Loading more...');
    this.loadMore();
  }
},

async loadMore() {
  if (this.loadingMore || !this.hasMore) return;
  
  this.loadingMore = true;
  
  try {
    const response = await axios.get('/api/v1/notifications', {
      params: { 
        limit: 20,  // Load 20 more each time
        offset: this.offset,
        ...(this.activeFilter !== 'all' && { notification_type: this.activeFilter })
      }
    });
    
    // Append new notifications
    this.notifications.push(...response.data.notifications);
    this.hasMore = response.data.has_more;
    this.offset += 20;
    
  } catch (error) {
    console.error('Failed to load more notifications:', error);
    this.lastError = 'Failed to load more notifications';
  } finally {
    this.loadingMore = false;
  }
},
    toggleDebugMode() {
      this.debugMode = !this.debugMode;
    },
    
    clearError() {
      this.lastError = null;
      this.hasError = false;
    },
    
    getFilterLabel(type) {
      const labels = {
        'new_comment': 'Comments',
        'screening_requested': 'Screening Requests',
        'screening_completed': 'Screenings Complete',
        'sourcing_comment_from_client': 'Client Comments',
'sourcing_progress':            'Sourcing Updates',
        'test': 'Test'
      };
      return labels[type] || type;
    },
    
    async runDebug() {
      try {
        const response = await axios.get('/api/v1/debug/notifications');
        console.log('Debug response:', response.data);
        alert(`Debug Info:\n${JSON.stringify(response.data, null, 2)}`);
      } catch (error) {
        console.error('Debug failed:', error);
        alert(`Debug failed: ${error.message}`);
      }
    },
    
    async createTestNotification() {
      try {
        const response = await axios.post('/api/v1/debug/create-test-notification');
        console.log('Test notification created:', response.data);
        alert('Test notification created!');
        // Refresh notifications
        await this.loadNotifications();
      } catch (error) {
        console.error('Failed to create test notification:', error);
        alert(`Failed: ${error.message}`);
      }
    },
    
    async detectUserRole() {
      try {
        const response = await axios.get('/api/v1/clients/user/type');
        this.userRole = response.data.user_type;
        this.currentUserId = response.data.user_id || 'unknown';
      } catch (error) {
        console.error('Error detecting user role:', error);
        this.userRole = 'agent';
        this.lastError = `User role detection failed: ${error.message}`;
        this.hasError = true;
      }
    },
    async handleFilterChange(newFilter) {
  this.activeFilter = newFilter;
  this.offset = 0;
  this.notifications = [];
  this.hasMore = true;
  await this.loadNotifications();
},

 

   
async loadNotifications(showLoading = true) {
      if (showLoading) {
        this.loading = true;
      }
      
      this.apiCallCount++;
      
      try {
        const response = await axios.get('/api/v1/notifications', {
          params: {  
            limit: 20,  // ✅ CHANGE from 50 to 20
            offset: 0  },// ✅ ADD - always start from 0 },
          timeout: 10000 // 10 second timeout
        });
        
        this.notifications = response.data.notifications || [];
        this.hasMore = response.data.has_more || false;  // ✅ ADD
        this.offset = 20;  // ✅ ADD - ready for next batch

        this.counts = response.data.counts || { total: 0, unread: 0, by_type: {} };
        this.apiStatus = 'success';
        this.lastRefresh = new Date().toLocaleTimeString();
        this.lastError = null;
        this.hasError = false;
        
        if (this.debugMode) {
          console.log('✅ Notifications loaded:', {
            count: this.notifications.length,
            unread: this.counts.unread,
            response: response.data
          });
        }
        
      } catch (error) {
        console.error('❌ Error loading notifications:', error);
        
        this.apiStatus = 'error';
        this.lastError = `API Error: ${error.response?.status || 'Network'} - ${error.message}`;
        this.hasError = true;
        
        // Emit error to parent
        this.$emit('show-notification', 'Failed to load notifications', 'error');
        
        if (this.debugMode) {
          console.log('❌ Full error details:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message
          });
        }
        
      } finally {
        this.loading = false;
      }
    }, 
    async refreshNotifications() {
      await this.loadNotifications(true);
    },
    
    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      
      if (this.showNotifications) {
        this.loadNotifications();
      }
    },
    
    closeNotifications() {
      const list = this.$refs.notificationsList;
  if (list) {
    list.removeEventListener('scroll', this.handleScroll);
    console.log('🧹 Scroll listener removed on close');
  }
  this.showNotifications = false;
},
    
    async markAsRead(notificationIds) {
      try {
        const response = await axios.post('/api/v1/notifications/mark-read', {
          notification_ids: notificationIds
        });
        
        // Update local state
        this.notifications.forEach(notification => {
          if (notificationIds.includes(notification.notification_id)) {
            notification.is_read = true;
            notification.read_at = new Date().toISOString();
          }
        });
        
        // Refresh counts
        await this.loadNotifications(false);
        
        if (this.debugMode) {
          console.log('✅ Marked as read:', response.data);
        }
        
      } catch (error) {
        console.error('❌ Error marking notifications as read:', error);
        this.lastError = `Mark as read failed: ${error.message}`;
        this.hasError = true;
        this.$emit('show-notification', 'Failed to mark notifications as read', 'error');
      }
    },
    
    async markAllAsRead() {
      try {
        const unreadIds = this.notifications
          .filter(n => !n.is_read)
          .map(n => n.notification_id);
        
        if (unreadIds.length > 0) {
          await this.markAsRead(unreadIds);
        }
      } catch (error) {
        console.error('Error marking all as read:', error);
      }
    },
    
    handleNotificationClick(notification) {
  // Mark as read
  if (!notification.is_read) {
    this.markAsRead([notification.notification_id]);
  }
  
  // Close notifications panel
  this.showDropdown = false;
  
  // Navigate based on notification type
  switch (notification.notification_type) {
    case 'hot_list_refreshed':
  // Navigate to hot list view
  if (notification.metadata?.hot_list_id) {
    this.$router.push({
      path: '/',
      query: {
        hot_list_id: notification.metadata.hot_list_id,
        auto_activate: 'true',
        from: 'notification'
      }
    });
  }
  break;
    case 'new_comment':
      if (notification.metadata?.candidate_id) {
        this.$router.push({
          path: '/',
          query: {
            project_id: notification.project_id,
            candidate_id: notification.metadata.candidate_id,
            auto_search: 'true',
            from: 'notification'
          }
        });
      }
      break;
      
    case 'screening_requested':
      // Handle screening request - can navigate to first candidate or project view
      if (notification.metadata?.candidate_ids && notification.metadata.candidate_ids.length > 0) {
        this.$router.push({
          path: '/',
          query: {
            project_id: notification.project_id,
            candidate_id: notification.metadata.candidate_ids[0], // Navigate to first candidate
            auto_search: 'true',
            from: 'notification'
          }
        });
      } else if (notification.project_id) {
        // Fallback to project view if no candidate_ids
        this.$router.push({
          path: '/',
          query: {
            project_id: notification.project_id,
            project_context: 'true',
            from: 'notification'
          }
        });
      }
      break;
      
    case 'screening_completed':
      // Handle screening completion
      if (notification.metadata?.candidate_id) {
        this.$router.push({
          path: '/',
          query: {
            project_id: notification.project_id,
            candidate_id: notification.metadata.candidate_id,
            auto_search: 'true',
            from: 'notification'
          }
        });
      }
      break; //if shit weird, remove this break

      // ✅ CASE for  new notification type
    case 'screening-in-progress':
      if (notification.metadata?.candidate_id) {
        this.$router.push({
          path: '/',
          query: {
            project_id: notification.project_id,
            candidate_id: notification.metadata.candidate_id,
            auto_search: 'true',
            from: 'notification'
          }
        });
      }
      break;
    case 'candidate_suggested':
      if (notification.metadata?.candidate_id) {
        this.$router.push({
          path: '/',
          query: {
            project_id: notification.project_id,
            candidate_id: notification.metadata.candidate_id,
            auto_search: 'true',
            from: 'notification'
          }
        });
      }
      break;
    case 'tech_screening_requested':
      if (notification.metadata?.candidate_id) {
        this.$router.push({
          path: '/',
          query: {
            project_id: notification.project_id,
            candidate_id: notification.metadata.candidate_id,
            auto_search: 'true',
            from: 'notification'
          }
        });
      }
      break;
case 'sourcing_comment_from_client':
case 'sourcing_progress':
case 'sourcing_resume_matched':
  this.$router.push({
    path: '/agent/projects',
    query: { highlight_project: notification.project_id }
  });
  break;
  case 'sourcing_comment_from_agent':
  this.$router.push({ path: '/client/projects' });
  break;
  case 'interview_requested':
case 'interview_scheduled':
case 'interview_done':
  // interview metadata uses elasticsearch_id, not candidate_id
  const interviewCandidateId = notification.metadata?.elasticsearch_id || notification.metadata?.candidate_id;
  if (interviewCandidateId && notification.project_id) {
    this.$router.push({
      path: '/',
      query: {
        project_id: notification.project_id,
        candidate_id: interviewCandidateId,
        auto_search: 'true',
        from: 'notification'
      }
    });
  }
  break;
    default:
      // Default to project view if no specific navigation
      if (notification.project_id) {
        this.$router.push({
          path: '/',
          query: {
            project_id: notification.project_id,
            project_context: 'true',
            from: 'notification'
          }
        });
      }
  }
},
    
    navigateToNotification(notification) {
      const metadata = notification.metadata || {};
      
      // Navigate to the relevant view based on notification type
      switch (notification.notification_type) {
        case 'new_comment':
        case 'screening_completed':
        case 'screening_requested':
        case 'screening-in-progress':
        case 'tech_screening_requested':
          if (metadata.candidate_id && notification.project_id) {
            this.$router.push({
              path: '/',
              query: {
                project_id: notification.project_id,
                candidate_id: metadata.candidate_id,
                auto_search: 'true',
                from: 'notification'
              }
            });
          }
          break;
          

        default:
          // Default to project view if no specific navigation
          if (notification.project_id) {
            this.$router.push({
              path: '/',
              query: {
                project_id: notification.project_id,
                project_context: 'true',
                from: 'notification'
              }
            });
          }
      }
    },
    
    getNotificationIcon(type) {
      const icons = {
        'new_comment': 'fas fa-comment text-blue-500',
        'screening_requested': 'fas fa-user-check text-yellow-500',
        'screening_completed': 'fas fa-check-circle text-green-500',
        'screening-in-progress': 'fas fa-cogs text-blue-500',  
        'project_status_change': 'fas fa-project-diagram text-purple-500',
        'test': 'fas fa-bug text-purple-500',
        'hot_list_refreshed': 'fas fa-fire text-orange-500',
        'sourcing_comment_from_client': 'fas fa-comment-dots text-purple-400',
        'sourcing_progress':            'fas fa-satellite-dish text-blue-400',
        'sourcing_resume_matched':      'fas fa-file-check text-green-400',
        'sourcing_comment_from_agent':  'fas fa-comment text-teal-400',
        'interview_requested':  'fas fa-calendar-alt text-yellow-400',
'interview_scheduled':  'fas fa-calendar-check text-green-400',
'interview_done':       'fas fa-handshake text-blue-400',
      };
      
      return icons[type] || 'fas fa-bell text-gray-500';
    },
    
    // formatTime(timestamp) {
    //   const now = new Date();
    //   const time = new Date(timestamp);
    //   const diff = now - time;
    //   const minutes = Math.floor(diff / 60000);
    //   const hours = Math.floor(diff / 3600000);
    //   const days = Math.floor(diff / 86400000);
      
    //   if (minutes < 1) return 'Just now';
    //   if (minutes < 60) return `${minutes}m ago`;
    //   if (hours < 24) return `${hours}h ago`;
    //   if (days < 7) return `${days}d ago`;
      
    //   return time.toLocaleDateString();
    // },
    formatTime(timestamp) {
  const now = Date.now();

  const time = new Date(timestamp.replace(' ', 'T') + 'Z');
  const diffSeconds = Math.floor((now - time.getTime()) / 1000);

  if (diffSeconds < 30) return 'Just now';

  const minutes = Math.floor(diffSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return time.toLocaleDateString();
}
,
    getViewAllLink() {
      // Return appropriate link based on user role
      return this.userRole === 'client' ? '/client/notifications' : '/agent/notifications';
    }
  }
};
</script>


<style scoped>
.notification-system {
  position: relative;
}

/* Add debug-specific styles */
.debug-panel {
  position: absolute;
  top: 100%;
  right: 0;
  background: #f0f0f0;
  border: 1px solid #ccc;
  padding: 1rem;
  border-radius: 0.5rem;
  width: 300px;
  z-index: 1001;
  font-size: 0.8rem;
  color: #333;
}

.debug-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  margin: 0.25rem;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.7rem;
}

.debug-toggle {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
}

.error-indicator {
  position: absolute;
  top: -2px;
  right: -2px;
  font-size: 0.75rem;
}

.error-display {
  background: #ffe6e6;
  color: #cc0000;
  padding: 0.5rem;
  margin: 0.5rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.clear-error {
  background: none;
  border: none;
  color: #cc0000;
  font-size: 1.2rem;
  cursor: pointer;
  margin-left: auto;
}

.notification-system {
  position: relative;
}

.notification-bell {
  position: relative;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 50%;
  transition: all 0.2s ease;
  color: var(--text-color);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-bell:hover {
  background: rgba(250, 249, 246, 0.1);
  transform: scale(1.05);
}

.notification-badge {
  position: relative;
  top: 0.25rem;
  right: 0.25rem;
  background: var(--primary-color);
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--background-color);
  animation: pulse-badge 2s infinite;
}

@keyframes pulse-badge {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.notifications-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  z-index: 1000;
  width: 400px;
  max-height: 600px;
  overflow: hidden;
}

.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
}

.notifications-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--surface-color);
}

.header-left h3 {
  margin: 0;
  color: var(--primary-color);
  font-size: 1rem;
  font-weight: 600;
}

.unread-indicator {
  font-size: 0.75rem;
  color: var(--primary-color);
  background: rgba(212, 47, 61, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  margin-left: 0.5rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.mark-read-btn, .refresh-btn {
  background: none;
  border: none;
  color: var(--text-color);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.mark-read-btn:hover, .refresh-btn:hover {
  background: rgba(250, 249, 246, 0.1);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.notification-filters {
  display: flex;
  padding: 0.5rem;
  gap: 0.25rem;
  background: var(--background-color);
  border-bottom: 1px solid var(--border-color);
}

.filter-btn {
  padding: 0.5rem 1rem;
  border: none;
  background: transparent;
  color: var(--text-color);
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-btn:hover {
  background: rgba(250, 249, 246, 0.1);
}

.filter-btn.active {
  background: var(--primary-color);
  color: white;
}

.filter-count {
  background: rgba(250, 249, 246, 0.2);
  padding: 0.125rem 0.375rem;
  border-radius: 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.filter-btn.active .filter-count {
  background: rgba(255, 255, 255, 0.2);
}

.notifications-list {
  max-height: 400px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.notification-item:hover {
  background: rgba(250, 249, 246, 0.03);
}

.notification-item.unread {
  background: rgba(212, 47, 61, 0.03);
  border-left: 3px solid var(--primary-color);
}

.notification-item.unread::before {
  content: '';
  position: absolute;
  left: 0.5rem;
  top: 1rem;
  width: 8px;
  height: 8px;
  background: var(--primary-color);
  border-radius: 50%;
}

.notification-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(250, 249, 246, 0.05);
  border-radius: 50%;
}

.notification-content {
  flex: 1;
}

.notification-title {
  font-weight: 600;
  color: var(--text-color);
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.notification-message {
  color: rgba(250, 249, 246, 0.8);
  font-size: 0.8125rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
}

.notification-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: rgba(250, 249, 246, 0.6);
}

.project-name {
  background: rgba(250, 249, 246, 0.1);
  padding: 0.125rem 0.5rem;
  border-radius: 0.75rem;
  font-size: 0.6875rem;
}

.notification-actions {
  flex-shrink: 0;
}

.mark-read-single {
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.mark-read-single:hover {
  background: rgba(212, 47, 61, 0.1);
}

.loading-state, .empty-state {
  padding: 2rem;
  text-align: center;
  color: rgba(250, 249, 246, 0.6);
}

.empty-state i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  display: block;
}

.notifications-footer {
  padding: 1rem;
  border-top: 1px solid var(--border-color);
  background: var(--surface-color);
}

.view-all-btn {
  display: block;
  text-align: center;
  color: var(--primary-color);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.75rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.view-all-btn:hover {
  background: rgba(212, 47, 61, 0.1);
}
.loading-more {
  padding: 1rem;
  text-align: center;
  color: rgba(250, 249, 246, 0.6);
  font-size: 0.875rem;
  background: var(--surface-color);
  border-top: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.loading-more i {
  color: var(--primary-color);
  font-size: 1rem;
}
</style>